(() => {
  const defineElement = (name, klass = class extends HTMLElement {}) => {
    if (!customElements.get(name)) {
      customElements.define(name, klass);
    }
  };

  function getCartDrawerSectionId() {
    const drawer = document.querySelector(
      "custom-cart-drawer[data-section-id]",
    );
    return drawer?.dataset.sectionId || "";
  }

  function getHeaderSectionId() {
    const headerSectionEl = document.querySelector("[data-header-section-id]");
    return headerSectionEl?.dataset.headerSectionId || "";
  }

  function getCartSections() {
    return [getCartDrawerSectionId(), getHeaderSectionId()]
      .filter(Boolean)
      .filter((id, index, arr) => arr.indexOf(id) === index)
      .join(",");
  }

  function getCartSectionsUrl() {
    return window.location.pathname || "/";
  }

  function addSectionsToPayload(payload) {
    const sections = getCartSections();

    if (!sections) {
      console.warn(
        "[el-product-form] No cart sections found. Make sure your cart drawer has data-section-id='{{ section.id }}'.",
      );
      return payload;
    }

    return {
      ...payload,
      sections,
      sections_url: getCartSectionsUrl(),
    };
  }

  function dispatchCartEvents(data) {
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:rerender", {
        detail: data,
        bubbles: true,
      }),
    );

    document.dispatchEvent(
      new CustomEvent("cartQuantityUpdated", {
        detail: data,
        bubbles: true,
      }),
    );

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: data,
        bubbles: true,
      }),
    );

    document.dispatchEvent(
      new CustomEvent("product:added", {
        detail: data,
        bubbles: true,
      }),
    );
  }

  class ElProductForm extends HTMLElement {
    constructor() {
      super();

      this.onSubmit = this.onSubmit.bind(this);
      this.onAtcClick = this.onAtcClick.bind(this);

      this.isSubmitting = false;
      this.stateTimer = null;
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.form = this.querySelector("[data-product-form-native]");
      if (!this.form) return;

      this.selectedVariantInput = this.querySelector(
        "[data-selected-variant-input]",
      );

      this.quantityInput = this.querySelector("[data-cart-quantity-input]");
      this.sellingPlanInput = this.querySelector("[data-selling-plan-input]");

      this.bundleOptions = Array.from(
        this.querySelectorAll("[data-bundle-option]"),
      );

      this.variantOptions = Array.from(
        this.querySelectorAll("[data-variant-option]"),
      );

      this.subscriptionToggle = this.querySelector(
        "[data-subscription-toggle]",
      );

      this.selectedVariantLabel = this.querySelector(
        "[data-selected-variant-label]",
      );

      this.atcButton = this.querySelector("[data-atc-button]");
      this.atcText = this.querySelector("[data-atc-text]");
      this.atcPrice = this.querySelector("[data-atc-price]");
      this.atcComparePrice = this.querySelector("[data-atc-compare-price]");

      this.baseButtonLabel = this.atcText?.dataset.baseLabel || "Add to cart";

      this.bundleOptions.forEach((option) => {
        option.addEventListener("click", () => this.selectBundle(option));
      });

      this.variantOptions.forEach((input) => {
        input.addEventListener("change", () => {
          if (input.checked) this.onVariantChange(input);
        });
      });

      if (this.subscriptionToggle) {
        this.subscriptionToggle.addEventListener("change", () => {
          this.updateAll();
        });
      }

      this.form.addEventListener("submit", this.onSubmit);

      if (this.atcButton) {
        this.atcButton.addEventListener("click", this.onAtcClick);
      }

      this.initDefaultBundle();
      this.initDefaultVariant();
      this.updateAll();
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener("submit", this.onSubmit);
      }

      if (this.atcButton) {
        this.atcButton.removeEventListener("click", this.onAtcClick);
      }

      clearTimeout(this.stateTimer);
    }

    isTrue(value) {
      return value === true || value === "true" || value === "1";
    }

    getBundleQty(option) {
      return Number(option?.dataset?.bundleQty || 1);
    }

    getBundleDiscount(option) {
      return Number(option?.dataset?.discount || 0);
    }

    initDefaultBundle() {
      if (!this.bundleOptions.length) {
        this.currentBundle = {
          el: null,
          qty: 1,
          discount: 0,
        };

        if (this.quantityInput) {
          this.quantityInput.value = 1;
        }

        return;
      }

      const preset = this.bundleOptions.find((option) =>
        this.isTrue(option.dataset.defaultSelected),
      );

      this.selectBundle(preset || this.bundleOptions[0], { silent: true });
    }

    initDefaultVariant() {
      const checked = this.variantOptions.find((input) => input.checked);

      if (checked) {
        this.onVariantChange(checked, { silent: true });
        return;
      }

      if (this.selectedVariantInput) {
        this.selectedVariantInput.value = this.dataset.defaultVariantId || "";
      }
    }

    selectBundle(option, { silent = false } = {}) {
      if (!option) return;

      this.bundleOptions.forEach((item) => {
        const active = item === option;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", active ? "true" : "false");
      });

      const qty = this.getBundleQty(option);
      const discount = this.getBundleDiscount(option);

      this.currentBundle = {
        el: option,
        qty,
        discount,
      };

      if (this.quantityInput) {
        this.quantityInput.value = qty;
      }

      if (!silent) {
        this.updateAll();
      }
    }

    onVariantChange(input, { silent = false } = {}) {
      this.currentVariantInput = input;

      if (this.selectedVariantInput) {
        this.selectedVariantInput.value = input.value;
      }

      if (this.selectedVariantLabel) {
        this.selectedVariantLabel.textContent =
          input.dataset.option1 || input.dataset.title || "";

        const variantText = input
          .closest("label")
          ?.querySelector(".el-variant-picker__text");

        const variantColor = variantText?.style.getPropertyValue(
          "--text-variant-color",
        );

        if (variantColor) {
          this.selectedVariantLabel.style.setProperty(
            "--text-variant-color",
            variantColor.trim(),
          );
          this.selectedVariantLabel.style.color = "var(--text-variant-color)";
        } else {
          this.selectedVariantLabel.style.removeProperty(
            "--text-variant-color",
          );
          this.selectedVariantLabel.style.color = "";
        }
      }

      this.querySelectorAll(".el-variant-picker__option").forEach((label) => {
        const radio = label.querySelector("[data-variant-option]");
        const active = radio === input && input.checked;
        label.classList.toggle("is-active", active);
      });

      if (!silent) {
        this.updateAll();
      }
    }

    getCurrentVariantData() {
      const input =
        this.currentVariantInput ||
        this.variantOptions.find((item) => item.checked);

      if (input) {
        return {
          id: input.value,
          available: this.isTrue(input.dataset.available),
          title: input.dataset.title || "",
          option1: input.dataset.option1 || input.dataset.title || "",
          price: Number(input.dataset.price || 0),
          compareAtPrice: Number(input.dataset.compareAtPrice || 0),
          spPrice: Number(input.dataset.spPrice || input.dataset.price || 0),
          spCompareAtPrice: Number(
            input.dataset.spCompareAtPrice || input.dataset.compareAtPrice || 0,
          ),
          sellingPlanId: input.dataset.sellingPlanId || "",
        };
      }

      return {
        id: this.dataset.defaultVariantId || "",
        available: this.isTrue(this.dataset.defaultVariantAvailable),
        title: this.dataset.defaultVariantTitle || "",
        option1:
          this.dataset.defaultVariantOption1 ||
          this.dataset.defaultVariantTitle ||
          "",
        price: Number(this.dataset.defaultVariantPrice || 0),
        compareAtPrice: Number(this.dataset.defaultVariantCompareAtPrice || 0),
        spPrice: Number(
          this.dataset.defaultVariantSpPrice ||
            this.dataset.defaultVariantPrice ||
            0,
        ),
        spCompareAtPrice: Number(
          this.dataset.defaultVariantSpCompareAtPrice ||
            this.dataset.defaultVariantCompareAtPrice ||
            0,
        ),
        sellingPlanId: this.dataset.defaultVariantSellingPlanId || "",
      };
    }

    isSubscriptionActive() {
      const variant = this.getCurrentVariantData();
      return !!this.subscriptionToggle?.checked && !!variant.sellingPlanId;
    }

    getPricingForBundle(bundleOption = this.currentBundle?.el) {
      const variant = this.getCurrentVariantData();

      const qty = bundleOption
        ? this.getBundleQty(bundleOption)
        : this.currentBundle?.qty || 1;

      const discount = bundleOption
        ? this.getBundleDiscount(bundleOption)
        : this.currentBundle?.discount || 0;

      const subscriptionActive = this.isSubscriptionActive();

      const basePrice = Number(
        subscriptionActive ? variant.spPrice : variant.price,
      );

      const rawCompareAt = Number(
        subscriptionActive ? variant.spCompareAtPrice : variant.compareAtPrice,
      );

      const compareUnitPrice =
        rawCompareAt > basePrice ? rawCompareAt : basePrice;

      // Shopify percentage discount allocation_method: "each"
      // Discount is calculated per unit, floored, then multiplied by quantity.
      const unitDiscount = Math.floor(basePrice * (discount / 100));

      const discountedUnitPrice = basePrice - unitDiscount;

      const priceTotal = discountedUnitPrice * qty;
      const compareTotal = compareUnitPrice * qty;
      const totalDiscount = unitDiscount * qty;

      return {
        qty,
        discount,
        unitDiscount,
        totalDiscount,
        unitPrice: discountedUnitPrice,
        priceTotal,
        compareTotal,
      };
    }

    updateSellingPlan() {
      if (!this.sellingPlanInput) return;

      const variant = this.getCurrentVariantData();

      const active =
        !!this.subscriptionToggle?.checked && !!variant.sellingPlanId;

      this.sellingPlanInput.value = active ? variant.sellingPlanId : "";
    }

    updateBundlePrices() {
      this.bundleOptions.forEach((option) => {
        const priceEl = option.querySelector("[data-bundle-price]");
        const compareEl = option.querySelector("[data-bundle-compare-price]");
        const pricing = this.getPricingForBundle(option);

        if (priceEl) {
          priceEl.textContent = this.formatMoney(pricing.priceTotal);
        }

        if (compareEl) {
          const showCompare = pricing.compareTotal > pricing.priceTotal;
          compareEl.textContent = this.formatMoney(pricing.compareTotal);
          compareEl.hidden = !showCompare;
        }
      });
    }

    updateAtcPrices() {
      const pricing = this.getPricingForBundle();

      if (this.atcPrice) {
        this.atcPrice.textContent = this.formatMoney(pricing.priceTotal);
      }

      if (this.atcComparePrice) {
        const showCompare = pricing.compareTotal > pricing.priceTotal;

        this.atcComparePrice.textContent = this.formatMoney(
          pricing.compareTotal,
        );

        this.atcComparePrice.hidden = !showCompare;
      }
    }

    updateButtonState(state = "default") {
      if (!this.atcButton || !this.atcText) return;

      const variant = this.getCurrentVariantData();
      const isAvailable = !!variant.available;

      clearTimeout(this.stateTimer);

      if (state === "adding") {
        this.atcText.textContent = "Adding...";
        this.atcButton.disabled = true;
        this.atcButton.classList.add("is-loading");
        this.atcButton.classList.remove("is-added");
        this.isSubmitting = true;
        return;
      }

      if (state === "added") {
        this.atcText.textContent = "Added";
        this.atcButton.disabled = true;
        this.atcButton.classList.remove("is-loading");
        this.atcButton.classList.add("is-added");
        this.isSubmitting = false;

        this.stateTimer = window.setTimeout(() => {
          this.updateButtonState("default");
        }, 1500);

        return;
      }

      if (state === "error") {
        this.atcText.textContent = "Failed to Add";
        this.atcButton.disabled = !isAvailable;
        this.atcButton.classList.remove("is-loading");
        this.atcButton.classList.remove("is-added");
        this.isSubmitting = false;

        this.stateTimer = window.setTimeout(() => {
          this.updateButtonState("default");
        }, 1500);

        return;
      }

      this.atcText.textContent = isAvailable
        ? this.baseButtonLabel
        : "Not Available";

      this.atcButton.disabled = this.isSubmitting || !isAvailable;
      this.atcButton.classList.remove("is-loading");
      this.atcButton.classList.remove("is-added");
    }

    updateAll() {
      this.updateSellingPlan();
      this.updateBundlePrices();
      this.updateAtcPrices();
      this.updateButtonState("default");
    }

    buildPayload() {
      if (!this.form) {
        throw new Error("Cart form not found.");
      }

      const variant = this.getCurrentVariantData();

      if (this.selectedVariantInput && variant.id) {
        this.selectedVariantInput.value = variant.id;
      }

      if (this.quantityInput && this.currentBundle?.qty) {
        this.quantityInput.value = this.currentBundle.qty;
      }

      this.updateSellingPlan();

      const formData = new FormData(this.form);

      const variantId = formData.get("id") || variant.id;
      const quantity = Number(
        formData.get("quantity") || this.currentBundle?.qty || 1,
      );

      const item = {
        id: Number(variantId),
        quantity,
      };

      if (!item.id) {
        throw new Error("Variant ID not found.");
      }

      const sellingPlan = formData.get("selling_plan");

      if (sellingPlan) {
        item.selling_plan = Number(sellingPlan);
      }

      const properties = {};

      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.*)\]$/);

        if (match && value !== "") {
          properties[match[1]] = value;
        }
      }

      if (Object.keys(properties).length) {
        item.properties = properties;
      }

      return addSectionsToPayload({
        items: [item],
      });
    }

    async submitViaAjax() {
      if (this.isSubmitting) return;

      const variant = this.getCurrentVariantData();

      if (!variant.available) {
        this.updateButtonState("default");
        return;
      }

      this.updateSellingPlan();
      this.updateButtonState("adding");

      try {
        const payload = this.buildPayload();

        const response = await fetch(
          (window.Shopify?.routes?.root || "/") + "cart/add.js",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify(payload),
          },
        );

        const data = await response.json();

        if (!response.ok) {
          throw data;
        }

        dispatchCartEvents(data);

        this.updateButtonState("added");
      } catch (error) {
        console.error("[el-product-form] Add to cart failed:", error);
        this.updateButtonState("error");
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      await this.submitViaAjax();
    }

    async onAtcClick(event) {
      const buttonType = this.atcButton?.getAttribute("type") || "submit";

      if (buttonType === "submit") {
        return;
      }

      event.preventDefault();
      await this.submitViaAjax();
    }

    formatMoney(cents) {
      const amount = Number(cents || 0);

      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(
          amount,
          this.dataset.moneyFormat || "${{amount}}",
        );
      }

      const currency =
        this.dataset.currencyCode || window.Shopify?.currency?.active || "USD";

      const locale = document.documentElement.lang || undefined;

      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(amount / 100);
    }
  }

  defineElement("el-product-form", ElProductForm);
  defineElement("el-bundle-quantity");
  defineElement("el-variant-picker");
  defineElement("el-subscription-cb");
  defineElement("el-atc-button");
})();
